import { registerComponent, debounce, asyncRequest, loading } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "product-options",
  class ProductOptions extends WYATTElement {
    ELEMENTS_TO_REPLACE = [
      "product-media",
      "product-prices",
      "add-to-cart",
      "inventory-messaging",
      "sku-display",
      "product-options",
      "product-badges",
      "price-table",
      "pricing-summary",
      "freight-estimator",
    ];

    constructor() {
      super();

      this.form = this.querySelector("form");
      this.options = Array.from(this.querySelectorAll("[data-product-option]"));
      [this.productPath, this.productParams] = this.config.url.split("?");
      this.isPDP = window.location.pathname === this.productPath;

      const debouncedHandler = debounce(
        this.handleOptionChange.bind(this),
        300,
      );

      this.form.addEventListener("change", debouncedHandler.bind(this));
    }

    get formData() {
      return new FormData(this.form);
    }

    get optionNames() {
      const names = Array.from(this.options).map(({ name }) => name);
      return [...new Set(names)];
    }

    get selectedOptionValues() {
      return this.optionNames.map((name) => this.formData.get(name));
    }

    get optionValueNamesById() {
      const names = {};

      this.options.forEach((input) => {
        names[input.value] = input.dataset.valueName;
      });

      return names;
    }

    get selectedOptionValueNames() {
      const namesById = this.optionValueNamesById;

      return this.config.product.options.map(
        (optionName) => namesById[this.formData.get(optionName)],
      );
    }

    get url() {
      const search = new URLSearchParams(this.productParams);
      search.set("option_values", this.selectedOptionValues.join(","));

      return `${window.location.origin}${
        this.productPath
      }?${search.toString()}`;
    }

    // Finds the variant matching the current option selection. If the
    // selection doesn't correspond to a real variant (eg. toggling a
    // "Blank" decoration option that isn't sold alongside the currently
    // selected values for the other options), falls back to the closest
    // available variant that still has the option value the customer just
    // chose.
    findVariant(changedOptionName, changedValueName) {
      const { variants, options: optionOrder } = this.config.product;
      const desiredNames = this.selectedOptionValueNames;
      const changedIndex = optionOrder.indexOf(changedOptionName);
      const changedKey = `option${changedIndex + 1}`;

      const exactMatch = variants.find((variant) =>
        optionOrder.every(
          (_, index) => variant[`option${index + 1}`] === desiredNames[index],
        ),
      );

      if (exactMatch) {
        return exactMatch;
      }

      const sameValueVariants = variants.filter(
        (variant) => variant[changedKey] === changedValueName,
      );
      const availableVariants = sameValueVariants.filter(
        (variant) => variant.available,
      );
      const candidates = availableVariants.length
        ? availableVariants
        : sameValueVariants;

      return candidates.reduce((best, variant) => {
        const score = optionOrder.filter(
          (_, index) => variant[`option${index + 1}`] === desiredNames[index],
        ).length;

        return !best || score > best.score ? { variant, score } : best;
      }, null)?.variant;
    }

    resolveUrl(optionName, valueName) {
      const variant = this.findVariant(optionName, valueName);

      if (!variant) {
        return this.url;
      }

      const search = new URLSearchParams(this.productParams);
      search.set("variant", variant.id);

      return `${window.location.origin}${
        this.productPath
      }?${search.toString()}`;
    }

    handleOptionChange(event) {
      const { target } = event;

      if (!target.matches("[data-product-option]")) {
        return;
      }

      const { productUrl, valueName } = target.dataset;
      const url = productUrl || this.resolveUrl(target.name, valueName);

      loading.add(this);

      this.updateProductDOM(url, productUrl);
    }

    async updateProductDOM(url, productUrl) {
      const response = await asyncRequest(url);

      if (response) {
        this.handleSuccessfulUpdate(response, url, productUrl);
      } else {
        this.handleUnsuccessfulUpdate(url, productUrl);
      }

      loading.remove(this);

      this.publish(WYATT.PubSub.events.updateOption, {});
    }

    handleSuccessfulUpdate(html, url, productUrl) {
      // TODO: Is conditional this needed?
      if (productUrl) {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const incoming = parsed.documentElement;

        this.updateDOM.replaceWith(document.documentElement, incoming);
      } else {
        const tempContainer = document.createElement("div");

        this.updateDOM.append(tempContainer, html);

        // TODO: Do we still need to make ID-related updates?
        tempContainer.querySelectorAll("[form]").forEach((element) => {
          element.setAttribute("form", this.config.addToCardFormID);
        });

        this.publish(
          WYATT.PubSub.events.updateDOM,
          this.ELEMENTS_TO_REPLACE.map((selector) => ({
            liveRegion: `${selector}-${this.config.product.id}`,
            html: tempContainer.innerHTML,
          })),
        );
      }

      if (this.isPDP) {
        window.history.replaceState(null, "", url);
      }
    }

    handleUnsuccessfulUpdate(url, productUrl) {
      if (this.isPDP) {
        window.location.href = productUrl || url;
      }
    }
  },
);
